import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Home,
  Calendar,
  Star,
  FileSignature,
  ArrowRight,
  Key,
  Users,
  MapPin,
} from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroAsset from "@/assets/solutions/real-estate/hero.webp.asset.json";
import bookingAsset from "@/assets/solutions/real-estate/booking.webp.asset.json";
import reviewsAsset from "@/assets/solutions/real-estate/reviews.webp.asset.json";
import nurtureAsset from "@/assets/solutions/real-estate/nurture.webp.asset.json";

const hero = heroAsset.url;
const booking = bookingAsset.url;
const reviews = reviewsAsset.url;
const nurture = nurtureAsset.url;

const heroAlt =
  "Luxury real estate agent sharing a Banana NFC business card with a couple at a property showroom to instantly send listings and booking link";
const bookingAlt =
  "Real estate agent profile on iPhone showing featured luxury apartment listings, Book a Private Tour button and one-tap contact exchange";
const reviewsAlt =
  "Real estate agent digital business card with automated 5-star Google review requests, AI-drafted replies and negative feedback intercepted privately";
const nurtureAlt =
  "Banana past-client nurture pipeline on iPhone showing closed real estate deal with scheduled home anniversary check-in, market updates and referral requests";

export const Route = createFileRoute("/solutions/real-estate")({
  head: () => ({
    meta: [
      { title: "NFC cards & CRM for Real Estate Agents — banana.cards" },
      {
        name: "description",
        content:
          "Tap to share listings, book showings on the spot, automate review requests, and stay top of mind with every past client for years.",
      },
      { property: "og:title", content: "NFC cards & CRM for Real Estate Agents — banana.cards" },
      {
        property: "og:description",
        content:
          "Listings, bookings, contracts and reviews — on a card you tap to share.",
      },
      { property: "og:image", content: hero },
      { property: "og:url", content: "https://banana.cards/solutions/real-estate" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/solutions/real-estate" }],
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="For Real Estate"
      title="Stay top of mind —"
      italic="for years."
      description="Tap your card at an open house, book the second viewing on the spot, send the contract in one click, and have the 5-star review request go out the day after closing — automatically."
      heroImage={hero}
      heroImageAlt={heroAlt}
      metrics={[
        { v: "4.9★", l: "Avg. agent review score" },
        { v: "+62%", l: "More listings from referrals" },
        { v: "7 years", l: "Average client nurture cycle" },
      ]}
      bullets={[
        {
          icon: Calendar,
          title: "Bookings on your card",
          desc: "Buyers tap, pick a showing time, and pay the deposit — all without leaving your branded profile.",
        },
        {
          icon: Star,
          title: "Review automation",
          desc: "After every closing, Banana sends the right review request (Google, Zillow, Realtor) at the right time, in your voice.",
        },
        {
          icon: FileSignature,
          title: "Contracts & disclosures, signed",
          desc: "Listing agreements, buyer rep, disclosures — all e-signed inside Banana, vaulted and ready for compliance.",
        },
      ]}
      deepDives={[
        {
          eyebrow: "First touch",
          title: "From open house",
          italic: "to second showing — in 30 seconds.",
          description:
            "Tap your NFC card on a visitor's phone. They see your listings, your reviews and your live calendar. They book the follow-up viewing before they leave the property.",
          points: [
            "Featured listings carousel on profile",
            "Live calendar with bookable slots",
            "Stripe deposits for serious buyers",
            "Auto-add to your nurture pipeline",
          ],
          image: booking,
          imageAlt: bookingAlt,
        },
        {
          eyebrow: "Reputation",
          title: "Be the agent with",
          italic: "100+ five-star reviews.",
          description:
            "Every closing triggers a sequence: SMS the next day for Google, email at day 3 for Zillow, soft re-ask at day 14 if no reply. You wake up to fresh reviews without lifting a finger.",
          points: [
            "Multi-platform review routing",
            "AI rewrites your reply in your tone",
            "Negative-feedback intercept (goes to you first)",
            "Embed widgets for your listing pages",
          ],
          image: reviews,
          imageAlt: reviewsAlt,
          reverse: true,
        },
        {
          eyebrow: "Nurture",
          title: "Stay in their life",
          italic: "for the next 7 years.",
          description:
            "Most past clients move every 5–7 years. Banana sends home-anniversary notes, neighborhood market updates, and birthday wishes — so when they're ready to sell, you're the only call.",
          points: [
            "Anniversary & birthday automations",
            "Quarterly neighborhood market reports",
            "Referral-request sequences",
            "Past-client tagging & segmentation",
          ],
          image: nurture,
          imageAlt: nurtureAlt,
        },
      ]}
      howItWorks={[
        { title: "Order your card", desc: "Branded metal or PVC with your logo, headshot and brokerage. Ships in 5–7 days." },
        { title: "Connect listings & calendar", desc: "Sync MLS feed, Google/Outlook calendar and your Stripe account for deposits." },
        { title: "Tap at every showing", desc: "Capture lead, book follow-up, kick off the nurture pipeline — in one tap." },
        { title: "Close & let it run", desc: "Contracts e-signed in Banana. Reviews trigger automatically. 7-year nurture starts." },
      ]}
      capabilities={[
        "Branded NFC business cards",
        "Featured listings carousel",
        "Booking & calendar sync",
        "Stripe deposit collection",
        "E-signed listing & buyer agreements",
        "Multi-platform review automation",
        "Past-client nurture sequences",
        "MLS & Zillow integrations",
      ]}
      faq={[
        {
          q: "Will it work with my MLS and Zillow account?",
          a: "Yes. Banana pulls your active listings from most major MLS feeds and from Zillow Premier Agent — they show up automatically on your profile and update in real time when status changes (active → pending → sold).",
        },
        {
          q: "Can buyers book a showing directly from my card?",
          a: "Yes. When someone taps your card, they see your live calendar (Google, Outlook or iCloud) with the time slots you've made available for showings. They pick one, optionally pay a deposit through Stripe, and the appointment lands on your calendar with all their contact info.",
        },
        {
          q: "How do contracts and disclosures work?",
          a: "Banana includes an e-signature engine that's compliant with ESIGN, UETA and eIDAS. Use our templates for listing agreements, buyer rep agreements and disclosures, or upload your brokerage's. Clients sign on their phone in under a minute. Signed PDFs are vaulted with an audit trail.",
        },
        {
          q: "What does the review automation actually send?",
          a: "After you mark a deal closed, a sequence kicks off: day 1 — friendly thank-you SMS with a Google review link; day 3 — email with a Zillow link if no Google review yet; day 14 — soft re-ask. The AI rewrites every message in your voice. Negative responses get intercepted and sent to you first, never publicly.",
        },
        {
          q: "How long does the past-client nurture run?",
          a: "Up to 10 years, automatically. Anniversaries, birthdays, quarterly neighborhood market updates and seasonal check-ins — all sent in your voice. Most agents see referral and repeat business pick up within 18 months of switching on the nurture.",
        },
      ]}
      outro={{
        title: "Be the agent",
        italic: "they remember.",
        description: "Every tap is a future listing. Every review is a future client. Every nurture is a referral waiting to happen.",
      }}
      afterFaq={<REUseCases />}
    />
  );
}

function REUseCases() {
  const items = [
    { i: Home, t: "Listing agents", d: "Tap-to-share listings, capture buyer interest, book showings without phone tag.", to: "/platform/bookings" },
    { i: Users, t: "Buyer's agents", d: "Pre-qualify on the spot, send disclosures, e-sign buyer rep in the parking lot.", to: "/platform/contracts" },
    { i: Key, t: "Property managers", d: "QR codes at every unit, maintenance requests routed automatically.", to: "/platform/networking/qr-code" },
    { i: MapPin, t: "Brokerages & teams", d: "Branded cards for the whole agent roster with central reporting & SSO.", to: "/solutions/enterprise" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Real estate use cases</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Every role in the brokerage, <em className="italic">covered.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Explore <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
